import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/i18n/client";
import { handleApiError } from "@/utils/shared";
import useCurrentUserManager from "./use-current-user-manager";

/**
 * Custom hook to handle API errors and optionally show a toast notification.
 *
 * @returns {Function} A function to handle API errors.
 *
 * @example
 * const handleApiError = useHandleApiError();
 *
 * // Usage
 * try {
 *   // API call
 * } catch (error) {
 *   handleApiError(error, true, "Custom error message");
 * }
 */
export default function useHandleApiError() {
  const { t: tGlobal } = useTranslation("global");
  const { toast } = useToast();
  const currentUserManager = useCurrentUserManager();

  const HandleApiError = useCallback(
    (error: Error, showToast = false, customMessage = "") => {
      const defaultErrors = {
        responseError: tGlobal("errors.api.default.responseError"),
        requestError: tGlobal("errors.api.default.requestError"),
        unknownError: tGlobal("errors.api.default.unknownError"),
      };
      const apiError = handleApiError(error, defaultErrors, customMessage);
      if (apiError.status === 401 || apiError.status === 403) {
        currentUserManager?.signoutRedirect();
      }
      if (showToast) {
        toast({
          description: apiError.message,
          variant: "destructive",
        });
      }
      return apiError;
    },
    [currentUserManager, tGlobal, toast]
  );

  return HandleApiError;
}
